import { computed } from 'vue'

import { useUserStore } from '@/store/user'


export const NEW_DIVISION_VALUES = [
  'qatar',
  'canada',
  'us',
  'tlc_global',
  'seattle',
  'no_division',
] as const

export type NewDivisionValue = typeof NEW_DIVISION_VALUES[number]

export type NewDivisionOption = {
  value: NewDivisionValue
  title?: string
  display_name?: string
  desc?: string
  icon?: {
    icon: string
    size?: string
  }
}

export const DEFAULT_NEW_DIVISION_OPTIONS: NewDivisionOption[] = [
  {
    icon: { icon: 'tabler-building-stadium', size: '40' },
    title: 'Qatar',
    display_name: 'Qatar',
    value: 'qatar',
    desc: 'Division - NetSuite ERP',
  },
  {
    icon: { icon: 'tabler-building', size: '40' },
    title: 'Canada',
    display_name: 'Canada',
    value: 'canada',
    desc: 'Division - Seradex ERP',
  },
  {
    icon: { icon: 'tabler-building-fortress', size: '40' },
    title: 'US',
    display_name: 'US',
    value: 'us',
    desc: 'Division - NetSuite ERP',
  },
  {
    icon: { icon: 'tabler-globe', size: '40' },
    title: 'TLC Global',
    display_name: 'TLC Global',
    value: 'tlc_global',
    desc: 'Division - NetSuite ERP',
  },
  {
    icon: { icon: 'tabler-building-estate', size: '40' },
    title: 'Seattle',
    display_name: 'Seattle',
    value: 'seattle',
    desc: 'Division - Epicor ERP',
  },
  {
    icon: { icon: 'tabler-buildings', size: '40' },
    title: 'No Division',
    display_name: 'No Division',
    value: 'no_division',
    desc: 'Division - No ERP',
  },
]

const NEW_DIVISION_VALUE_SET = new Set<string>(NEW_DIVISION_VALUES)

export const isNewDivisionValue = (value: unknown): value is NewDivisionValue => (
  typeof value === 'string' && NEW_DIVISION_VALUE_SET.has(value)
)

export const normalizeNewDivisionValues = (value: unknown): NewDivisionValue[] => {
  const values = Array.isArray(value)
    ? value
    : value
      ? [value]
      : []

  const normalizedValues = values
    .map(item => String(item))
    .filter(isNewDivisionValue)

  return NEW_DIVISION_VALUES.filter(item => normalizedValues.includes(item))
}

export const filterNewDivisionOptions = <T extends { value?: string }>(items: T[] = []): T[] => {
  return items.filter(item => isNewDivisionValue(item?.value))
}

export const filterAllowedNewDivisionOptions = <T extends { value?: string }>(
  items: T[] = [],
  allowedDivisions: unknown,
): T[] => {
  const allowedDivisionSet = new Set(normalizeNewDivisionValues(allowedDivisions))

  return filterNewDivisionOptions(items).filter(item => allowedDivisionSet.has(item.value as NewDivisionValue))
}

export const isNewDivisionSurface = (items: Array<{ value?: string }> = []): boolean => {
  return items.some(item => item.value === 'tlc_global' || item.value === 'no_division')
}

export const getPreferredNewDivision = (allowedDivisions: unknown): NewDivisionValue | null => {
  return normalizeNewDivisionValues(allowedDivisions)[0] ?? null
}

export function useNewDivisionAccess() {
  const userStore = useUserStore()

  const normalizedUserDivisions = computed(() => normalizeNewDivisionValues(userStore.user?.divisions))

  const allowedNewDivisionOptions = computed(() => (
    filterAllowedNewDivisionOptions(DEFAULT_NEW_DIVISION_OPTIONS, normalizedUserDivisions.value)
  ))

  const preferredNewDivision = computed(() => normalizedUserDivisions.value[0] ?? null)

  return {
    normalizedUserDivisions,
    allowedNewDivisionOptions,
    preferredNewDivision,
    filterNewDivisionOptions,
    filterAllowedNewDivisionOptions,
    isNewDivisionSurface,
    isNewDivisionValue,
  }
}
